import Audio from "./Audio";

/**
 * @class
 * @memberOf P3.audio
 */
export default class AudioManager {
    constructor() {
        /**
         * @member {Object}
         * @private
         */
        this._cache = {};

        /**
         * @member {Audio}
         * @private
         */
        this._music = null;

        /**
         * @member {boolean}
         * @private
         */
        this._isMute = false;

        let hidden;
        "undefined" !== typeof document.hidden ? (hidden = "hidden",
            this.visibilityChange = "visibilitychange") : "undefined" !== typeof document.mozHidden ? (hidden = "mozHidden",
            this.visibilityChange = "mozvisibilitychange") : "undefined" !== typeof document.msHidden ? (hidden = "msHidden",
            this.visibilityChange = "msvisibilitychange") : "undefined" !== typeof document.webkitHidden && (hidden = "webkitHidden",
            this.visibilityChange = "webkitvisibilitychange");

        document.addEventListener(this.visibilityChange, function(){
            document[hidden] ? Howler.volume(0) : Howler.volume(1);
        }, false);
    }

    /**
     * @param {Array.<string>} sounds
     * @param {Array.<string>} extensions
     * @param {string} [basePath]
     */
    addSounds(sounds, extensions, basePath) {
        basePath = basePath || "";

        for (let i = 0; i < sounds.length; ++ i) {
            let url = basePath + sounds[i];
            url = url.split("/");
            const name = url[url.length - 1];

            const urls = [];
            for (let j = 0; j < extensions.length; ++ j) {
                const extension = extensions[j];
                urls.push(url.join("/") + extension);
            }
            const howl = new Howl({
                src: urls,
                volume: 1.0,
                loop: false,
                autoplay: false
            });
            howl.name = name;
            this._cache[name] = howl;
        }
    }

    /**
     * @param {string} name
     * @param {string} sprite
     * @param {Array.<string>} extensions
     * @param {string} [basePath]
     */
    addSoundSprite(name, sprite, extensions, basePath) {
        basePath = basePath || "";

        let url = basePath + name;
        url = url.split("/");

        const urls = [];
        for (let i = 0; i < extensions.length; ++ i) {
            const extension = extensions[i];
            urls.push(url.join("/") + extension);
        }

        const howl = new Howl({
            src: urls,
            sprite: sprite,
            volume: 1,
            loop: false,
            autoplay: false
        });
        howl.isSoundSprite = true;
        for (let i in sprite) {
            if (sprite.hasOwnProperty(i)) {
                this._cache[i] = howl;
            }
        }
    }

    /**
     * @param name
     * @param {object} params
     * @param {number} [params.volume=1]
     * @param {boolean} [params.loop=false]
     * @param {function} [params.callback]
     * @param {*} [params.scope]
     */
    playSound(name, params) {
        params = Object.assign({}, {volume: 1, loop: false}, params);
        if (typeof name !== "string") {
            name = P3.utils.selectRandom(name);
        }

        let id = -1;
        const howl = this._cache[name];
        if (!howl) {
            console.warn("Could not find sound - " + name);
            return id;
        }

        id = howl.isSoundSprite ? howl.play(name) : howl.play();
        howl.volume(params.volume || 1, id);
        howl.loop(params.loop || false, id);

        const audio = new Audio(id, howl);
        if (params.callback) {
            audio._onend = function() {
                params.callback.call(params.scope || window);
            };
            howl.on("end", audio._onend);
        }
        return audio;
    }

    /**
     * @param name
     * @param {object} params
     * @param {number} [params.volume=1]
     * @param {number} [params.delay=0]
     * @param {number} [params.fadeIn=0]
     * @param {function} [params.callback]
     * @param {*} [params.scope]
     */
    playMusic(name, params) {
        params = Object.assign({}, {volume: 1, delay: 0, fadeIn: 0}, params);
        if (typeof name !== "string") {
            name = P3.utils.selectRandom(name);
        }

        let id = -1;

        // skip if no change
        if (this._music && this._music.howl.name === name) {
            return this._music;
        }

        const howl = this._cache[name];
        if (!howl) {
            console.warn("Could not find music - " + name);
            return null;
        }

        id = howl.play();
        howl.volume(params.volume || 1, id);
        howl.loop(true, id);

        const audio = new Audio(id, howl);
        if (params.callback) {
            audio._onend = function() {
                params.callback.call(params.scope);
            };
            howl.on("end", audio._onend);
        }

        if ((params.fadeIn || 0) > 0) {
            if (this._music) {
                this.fadeOut(this._music, params.fadeIn, function(audio) {
                    audio.destroy();
                }, this);
            }
            this.fadeIn(audio, params.fadeIn);
        } else if (this._music) {
            this._music.destroy();
        }

        this._music = audio;
        return audio;
    }

    stopMusic() {
        if (!this._music) return;
        this._music.destroy();
        this._music = null;
    }

    /**
     * @param {boolean} value
     */
    mute(value) {
        this._isMute = value;
        if (this._isMute) {
            Howler.mute(true);
        } else {
            Howler.mute(false);
        }
    }

    /**
     * @param {Audio} audio
     * @param {number} [duration=1]
     * @param {function} [callback]
     * @param {*} [scope]
     */
    fadeIn(audio, duration = 1, callback, scope) {
        const volume = audio.howl.volume(audio.id);
        audio.howl.volume(0, audio.id);
        audio.__volume = 0;

        TweenMax.killTweensOf(audio);
        TweenMax.to(audio, duration, {
            __volume: volume,
            ease: Power1.easeInOut,
            onUpdate: function() {
                audio.howl.volume(audio.__volume, audio.id);
            },
            onUpdateScope: this,
            onComplete: callback,
            onCompleteParams: [audio],
            onCompleteScope: scope
        });
    }

    /**
     * @param {Audio} audio
     * @param {number} [duration=1]
     * @param {function} [callback]
     * @param {*} [scope]
     */
    fadeOut(audio, duration = 1, callback, scope) {
        audio.__volume = audio.howl.volume(audio.id);

        TweenMax.killTweensOf(audio);
        TweenMax.to(audio, duration, {
            __volume: 0,
            ease: Power1.easeInOut,
            onUpdate: function() {
                audio.howl.volume(audio.__volume, audio.id);
            },
            onUpdateScope: this,
            onComplete: callback,
            onCompleteParams: [audio],
            onCompleteScope: scope
        });
    }

    /**
     * @returns {Audio}
     */
    get music() {
        return this._music;
    }

    /**
     * @returns {boolean}
     */
    get isMute() {
        return this._isMute;
    }
}